import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { clearCart } from '../../../shared/store/cartSlice';
import { orderService } from '../services/orderService';
import type { ShippingAddress } from '../types/orderTypes';

const DELHI_VARIANTS = ['delhi', 'new delhi', 'delhi ncr', 'ncr delhi'];
function getGstRate(state: string): number {
    return DELHI_VARIANTS.includes(state.trim().toLowerCase()) ? 5 : 12;
}

const API_STATIC = (process.env.REACT_APP_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/api\/v1.*$/, '');

const INDIA_STATES = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
    'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
    'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab',
    'Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
    'Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh',
    'Chandigarh','Puducherry',
];

type Step = 'address' | 'review';

const empty: ShippingAddress = { name: '', phone: '', flat: '', street: '', landmark: '', city: '', state: '', pin: '', country: 'India' };

const CheckoutPage: React.FC = () => {
    const dispatch   = useAppDispatch();
    const navigate   = useNavigate();
    const items      = useAppSelector(s => s.cart.items);
    const authUser   = useAppSelector(s => s.auth.user);

    const [step, setStep]             = useState<Step>('address');
    const [address, setAddress]       = useState<ShippingAddress>({ ...empty, name: authUser?.name ?? '' });
    const [errors, setErrors]         = useState<Partial<Record<keyof ShippingAddress, string>>>({});
    const [placing, setPlacing]       = useState(false);
    const [apiError, setApiError]     = useState('');
    const [courierCharge, setCourierCharge] = useState(0);

    useEffect(() => {
        orderService.getShippingPreview()
            .then(res => setCourierCharge(res.data.courierCharge))
            .catch(() => {});
    }, []);

    const subtotal   = items.reduce((s, i) => s + i.price * i.qty, 0);
    const totalItems = items.reduce((s, i) => s + i.qty, 0);
    const gstRate    = address.state ? getGstRate(address.state) : 12;
    const gstAmount  = Math.round(subtotal * gstRate) / 100;
    const totalAmount = subtotal + gstAmount + courierCharge;

    if (!items.length) {
        return (
            <div style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Open Sans', sans-serif", padding: 40 }}>
                <h2 style={{ color: '#1a202c', marginBottom: 12 }}>Your cart is empty</h2>
                <Link to="/products" style={{ color: '#1565c0', fontWeight: 600 }}>← Browse Products</Link>
            </div>
        );
    }

    /* ── Validation ─────────────────────────────────────────────────── */
    const validate = (): boolean => {
        const e: typeof errors = {};
        if (!address.name.trim())  e.name  = 'Full name is required';
        if (!address.phone.trim()) e.phone = 'Phone number is required';
        else if (!/^\d{10}$/.test(address.phone.replace(/\s/g, ''))) e.phone = 'Enter a valid 10-digit mobile number';
        if (!address.flat.trim())   e.flat   = 'House / Flat is required';
        if (!address.street.trim()) e.street = 'Street / Area is required';
        if (!address.city.trim())   e.city   = 'City is required';
        if (!address.state.trim())  e.state  = 'State is required';
        if (!address.pin.trim())    e.pin    = 'PIN code is required';
        else if (!/^\d{6}$/.test(address.pin)) e.pin = 'Enter a valid 6-digit PIN';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleAddressContinue = () => {
        if (validate()) setStep('review');
    };

    const handlePlaceOrder = async () => {
        setApiError('');
        setPlacing(true);
        try {
            const { data } = await orderService.placeOrder(items, address);
            dispatch(clearCart());
            navigate(`/order-confirmation/${data.order._id}`, { state: { order: data.order } });
        } catch (err: any) {
            setApiError(err?.response?.data?.message ?? 'Failed to place order. Please try again.');
        } finally {
            setPlacing(false);
        }
    };

    return (
        <div style={{ background: '#f8fafc', minHeight: '60vh', padding: '40px 0 80px', fontFamily: "'Open Sans', sans-serif" }}>
            <div className="container">

                {/* Breadcrumb */}
                <nav style={{ marginBottom: 24, fontSize: '0.85rem', color: '#6b7280' }}>
                    <Link to="/" style={{ color: '#1565c0', textDecoration: 'none' }}>Home</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <Link to="/cart" style={{ color: '#1565c0', textDecoration: 'none' }}>Cart</Link>
                    <span style={{ margin: '0 8px' }}>›</span>
                    <span style={{ color: '#1a202c', fontWeight: 600 }}>Checkout</span>
                </nav>

                {/* Step indicator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
                    <StepDot n={1} label="Delivery Address" active={step === 'address'} done={step === 'review'} onClick={() => setStep('address')} />
                    <div style={{ flex: 1, height: 2, background: step === 'review' ? '#1565c0' : '#e2e8f0', maxWidth: 80, margin: '0 8px' }} />
                    <StepDot n={2} label="Review & Pay" active={step === 'review'} done={false} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}>

                    {/* ── Left panel ── */}
                    <div>
                        {step === 'address' && (
                            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 28 }}>
                                <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a202c', margin: '0 0 24px' }}>Delivery Address</h2>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <Field label="Full Name *" error={errors.name}>
                                        <input style={inputStyle(!!errors.name)} value={address.name} onChange={e => setAddress(a => ({ ...a, name: e.target.value }))} placeholder="Ramesh Kumar" />
                                    </Field>
                                    <Field label="Mobile Number *" error={errors.phone}>
                                        <input style={inputStyle(!!errors.phone)} value={address.phone} onChange={e => setAddress(a => ({ ...a, phone: e.target.value }))} placeholder="9876543210" maxLength={10} />
                                    </Field>
                                    <Field label="House / Flat / Building *" error={errors.flat} full>
                                        <input style={inputStyle(!!errors.flat)} value={address.flat} onChange={e => setAddress(a => ({ ...a, flat: e.target.value }))} placeholder="Flat 4B, Green Heights" />
                                    </Field>
                                    <Field label="Street / Area / Colony *" error={errors.street} full>
                                        <input style={inputStyle(!!errors.street)} value={address.street} onChange={e => setAddress(a => ({ ...a, street: e.target.value }))} placeholder="MG Road, Sector 14" />
                                    </Field>
                                    <Field label="Landmark (optional)">
                                        <input style={inputStyle(false)} value={address.landmark ?? ''} onChange={e => setAddress(a => ({ ...a, landmark: e.target.value }))} placeholder="Near Metro Station" />
                                    </Field>
                                    <Field label="City *" error={errors.city}>
                                        <input style={inputStyle(!!errors.city)} value={address.city} onChange={e => setAddress(a => ({ ...a, city: e.target.value }))} placeholder="New Delhi" />
                                    </Field>
                                    <Field label="State *" error={errors.state}>
                                        <select style={inputStyle(!!errors.state)} value={address.state} onChange={e => setAddress(a => ({ ...a, state: e.target.value }))}>
                                            <option value="">Select state</option>
                                            {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="PIN Code *" error={errors.pin}>
                                        <input style={inputStyle(!!errors.pin)} value={address.pin} onChange={e => setAddress(a => ({ ...a, pin: e.target.value }))} placeholder="110001" maxLength={6} />
                                    </Field>
                                    <Field label="Country">
                                        <input style={{ ...inputStyle(false), background: '#f8fafc', color: '#6b7280' }} value="India" readOnly />
                                    </Field>
                                </div>

                                <button
                                    onClick={handleAddressContinue}
                                    style={{ marginTop: 24, padding: '12px 36px', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 50, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}
                                >
                                    Continue to Review →
                                </button>
                            </div>
                        )}

                        {step === 'review' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                                {/* Address summary */}
                                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a202c', margin: 0 }}>Deliver to</h3>
                                        <button onClick={() => setStep('address')} style={{ background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Change</button>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151', lineHeight: 1.6 }}>
                                        <strong>{address.name}</strong> · {address.phone}<br />
                                        {address.flat}, {address.street}{address.landmark ? `, ${address.landmark}` : ''}<br />
                                        {address.city}, {address.state} – {address.pin}, {address.country}
                                    </p>
                                </div>

                                {/* Payment method */}
                                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a202c', margin: '0 0 12px' }}>Payment Method</h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#eff6ff', border: '1.5px solid #1565c0', borderRadius: 10 }}>
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                                        </svg>
                                        <div>
                                            <p style={{ margin: 0, fontWeight: 700, color: '#1565c0', fontSize: '0.9rem' }}>Online Payment</p>
                                            <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#64748b' }}>Credit / Debit Card, UPI, Net Banking</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Items */}
                                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
                                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a202c', margin: '0 0 16px' }}>
                                        Order Items ({totalItems})
                                    </h3>
                                    {items.map(item => (
                                        <div key={item.productId} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                                            <img
                                                src={`${API_STATIC}${item.image}`}
                                                alt={item.name}
                                                style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', background: '#f8fafc' }}
                                                onError={e => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect width="60" height="60" fill="%23f0f4f8"/%3E%3C/svg%3E'; }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <p style={{ margin: '0 0 2px', fontSize: '0.9rem', fontWeight: 600, color: '#1a202c' }}>{item.name}</p>
                                                <p style={{ margin: '0 0 4px', fontSize: '0.78rem', color: '#6b7280' }}>{item.brand}</p>
                                                <p style={{ margin: 0, fontSize: '0.82rem', color: '#374151' }}>Qty: {item.qty}</p>
                                            </div>
                                            <span style={{ fontWeight: 700, color: '#1a202c', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                                                ₹{(item.price * item.qty).toLocaleString('en-IN')}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Price breakdown */}
                                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
                                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#374151', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Price Details</h3>
                                    <PriceRow label={`Price (${totalItems} items)`} value={subtotal} />
                                    <PriceRow label={`GST @ ${gstRate}%`} value={gstAmount} hint={gstRate === 5 ? 'Intra-state (Delhi)' : 'IGST (Inter-state)'} />
                                    <PriceRow label="Courier Charges" value={courierCharge} green={courierCharge === 0} freeLabel={courierCharge === 0} />
                                    <hr style={{ border: 'none', borderTop: '2px solid #e2e8f0', margin: '10px 0 12px' }} />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>
                                        <span>Total Amount</span>
                                        <span>₹{totalAmount.toLocaleString('en-IN')}</span>
                                    </div>
                                    {gstRate === 5 ? (
                                        <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#64748b' }}>5% GST applied (Delhi — same state as seller)</p>
                                    ) : (
                                        <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#64748b' }}>12% IGST applied (delivery outside Delhi)</p>
                                    )}
                                </div>

                                {apiError && (
                                    <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: '0.88rem', fontWeight: 500 }}>
                                        {apiError}
                                    </div>
                                )}

                                <button
                                    onClick={handlePlaceOrder}
                                    disabled={placing}
                                    style={{ padding: '14px 0', background: placing ? '#93c5fd' : '#1565c0', color: '#fff', border: 'none', borderRadius: 50, fontWeight: 700, fontSize: '1rem', cursor: placing ? 'default' : 'pointer', transition: 'background 0.15s' }}
                                >
                                    {placing ? 'Placing Order…' : 'Place Order →'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Order summary sidebar ── */}
                    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, position: 'sticky', top: 100 }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a202c', margin: '0 0 16px' }}>Order Summary</h3>

                        {items.map(item => (
                            <div key={item.productId} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.85rem', color: '#374151' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>{item.name} × {item.qty}</span>
                                <span style={{ fontWeight: 600, flexShrink: 0 }}>₹{(item.price * item.qty).toLocaleString('en-IN')}</span>
                            </div>
                        ))}

                        <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '14px 0' }} />
                        <PriceRow label={`Subtotal (${totalItems} items)`} value={subtotal} />
                        <PriceRow
                            label={`GST ${gstRate}% ${gstRate === 5 ? '(Intra-state)' : '(IGST)'}`}
                            value={gstAmount}
                            hint={gstRate === 5 ? 'Delhi delivery' : undefined}
                        />
                        <PriceRow label="Courier Charges" value={courierCharge} green={courierCharge === 0} freeLabel={courierCharge === 0} />
                        <hr style={{ border: 'none', borderTop: '2px solid #e2e8f0', margin: '10px 0 12px' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.05rem', color: '#1a202c' }}>
                            <span>Total</span>
                            <span>₹{totalAmount.toLocaleString('en-IN')}</span>
                        </div>

                        <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 14, lineHeight: 1.5 }}>
                            By placing your order, you agree to Arte Craft India's conditions of use and privacy policy.
                        </p>
                    </div>

                </div>
            </div>

            <style>{`
                @media (max-width: 768px) {
                    .container > div[style*="grid-template-columns: 1fr 340px"] {
                        grid-template-columns: 1fr !important;
                    }
                    .container > div > div > div > div[style*="grid-template-columns: 1fr 1fr"] {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </div>
    );
};

/* ── Small helpers ──────────────────────────────────────────────────── */

const PriceRow: React.FC<{ label: string; value: number; hint?: string; green?: boolean; freeLabel?: boolean }> = ({ label, value, hint, green, freeLabel }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, fontSize: '0.88rem' }}>
        <span style={{ color: '#4b5563' }}>
            {label}
            {hint && <span style={{ color: '#94a3b8', fontSize: '0.75rem', marginLeft: 6 }}>({hint})</span>}
        </span>
        {freeLabel
            ? <span style={{ color: '#16a34a', fontWeight: 600 }}>FREE</span>
            : <span style={{ color: green ? '#16a34a' : '#1a202c', fontWeight: 600 }}>₹{value.toLocaleString('en-IN')}</span>
        }
    </div>
);

const StepDot: React.FC<{ n: number; label: string; active: boolean; done: boolean; onClick?: () => void }> = ({ n, label, active, done, onClick }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: (done && onClick) ? 'pointer' : 'default' }} onClick={done && onClick ? onClick : undefined}>
        <div style={{
            width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: done ? '#1565c0' : active ? '#1565c0' : '#e2e8f0',
            color: (done || active) ? '#fff' : '#94a3b8',
            fontWeight: 700, fontSize: '0.85rem',
        }}>
            {done ? '✓' : n}
        </div>
        <span style={{ fontSize: '0.85rem', fontWeight: active || done ? 600 : 400, color: active || done ? '#1a202c' : '#94a3b8' }}>
            {label}
        </span>
    </div>
);

const Field: React.FC<{ label: string; error?: string; full?: boolean; children: React.ReactNode }> = ({ label, error, full, children }) => (
    <div style={{ gridColumn: full ? '1 / -1' : undefined, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>{label}</label>
        {children}
        {error && <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>{error}</span>}
    </div>
);

function inputStyle(hasError: boolean): React.CSSProperties {
    return {
        padding: '10px 12px', borderRadius: 8, fontSize: '0.9rem',
        border: `1.5px solid ${hasError ? '#fca5a5' : '#d1d5db'}`,
        background: hasError ? '#fef2f2' : '#fff', outline: 'none',
        width: '100%', boxSizing: 'border-box' as const,
        fontFamily: "'Open Sans', sans-serif",
    };
}

export default CheckoutPage;
